// Role IDs
export const ROLES = {
  EXPERIMENTAL_OWNER:0,
  OWNER: 1,
  ADMIN: 2,
  HR: 3,
  OPERATIONS: 4,
  LEAD:5,
  FLEET_MANAGER: 8, 
  DRIVER: 10,
  // Provider roles (20-30)
  FLEET_PROVIDER_OWNER: 21,
  FLEET_PROVIDER_ADMIN: 22,
  FLEET_PROVIDER_TECHNICIAN: 23,
  FINANCE_PROVIDER_OWNER: 31,
  FINANCE_PROVIDER_ADMIN: 32,
  FINANCE_PROVIDER_ANALYST: 33,
  LINQOPS_ADMIN: 201,
} as const;

// Role Names by ID
export const ROLE_NAMES: { [key: number]: string } = {
  0: 'Experimental Owner',
  1: 'Owner',
  2: 'Admin',
  3: 'HR',
  4: 'Operations',
  5: 'Lead',
  8: 'Fleet Manager',
  10: 'Driver',
  // Provider roles
  21: 'Fleet Provider Owner',
  22: 'Fleet Provider Admin',
  23: 'Fleet Provider Technician',
  31: 'Finance Provider Owner',
  32: 'Finance Provider Admin',
  33: 'Finance Provider Analyst',
  201: 'LinqOps Admin',
};

export const ROLE_RANGES = {
  PROVIDER: {
    MIN: 20,
    MAX: 209
  },
  PROVIDER_FLEET: {
    MIN: 20,
    MAX: 29
  },
  PROVIDER_FINANCE: {
    MIN: 30,
    MAX: 39
  },
  LINQOPS_ADMIN: {
    MIN: 201,
    MAX: 209
  }
};

// Employee Status
export const STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  OFFBOARDED: 'OFFBOARDED',
  'Background Check in Progress': 'Background Check in Progress',
} as const;

// Default Values
export const DEFAULTS = {
  TIMEZONE: 'America/New_York',
} as const;

// System User ID
export const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000001';
export const SYSTEM_USER_EMAIL = 'bot@linqops.com';

export const VEHICLE_STATUS = {
  INACTIVE: 'INACTIVE',
  ACTIVE: 'ACTIVE',
} as const;

export const VEHICLE_OPERATIONAL_STATUS = {
  OPERATIONAL: 'OPERATIONAL',
  GROUNDED: 'GROUNDED',
} as const;

export const EMPLOYEE_NOTES_TYPE = {
  ATTENDANCE: 'attendance',
  PERFORMANCE: 'performance',
  BEHAVIOR: 'behavior',
  FLEET: 'fleet',
  DOG_BITE: 'dog-bite',
  INCIDENT: 'incident',
  INJURY: 'injury',
  UNIFORM:'uniform',
  DRUG_TEST: 'drug-test',
  SCHEDULE:'schedule',
  OTHER: 'other',
} as const;

// Attendance note type mapping for display labels
export const ATTENDANCE_TYPE_LABELS: Record<string, string> = {
  'call_out': 'Call Out',
  'late': 'Late',
  'no_show': 'No Show',
  'vto': 'VTO'
};

export const APPLICANT_NOTES_TYPE = {
  ATTENDANCE: 'attendance',
  PERFORMANCE: 'performance',
  BEHAVIOR: 'behavior',
  FLEET: 'fleet',
  OTHER: 'other',
} as const;

export const FLEET_NOTES_TYPE = {
  REPAIR: "repair",
  PREVENTATIVE_MAINTENANCE: "preventative-maintenance",
  INSPECTION: "inspection",
  PAVE:"pave",
  ACCIDENT: "accident",
  DRIVER: "driver",
  DISPUTE:"dispute",
  TECHNICIAN: "technician",
  OTHER: "other",
};

export const OPERATION_DASHBOARD_OPTIONS = {
  ROUTES:"Routes",
  DAS:"DAs"
} as const;

export const FLEET_NOTES_SUB_TYPE = {
  DISPUTE: "dispute/afs",
};
export const FLEET_DOCUMENT_TYPE = {
  REGISTRATION: 'registration',
  INSURANCE: 'insurance',
  INSPECTION: 'inspection',
  RENTAL_AGREEMENT: 'rental-agreement',
  INVOICE: 'invoice',
  OTHER: 'other',
} as const;

export const EMPLOYEE_STATUS_TYPE = {
  Active: 'ACTIVE',
  Inactive: 'INACTIVE',
  Offboarded: 'OFFBOARDED',
  Onboarding: 'ONBOARDING',
} as const;

export const getStatusText = (progressStatus: string): string => {
  switch (progressStatus) {
    case "not_applicable":
      return "Not Applicable";
    case "complete":
      return "Complete";
    case "ahead":
      return "Ahead";
    case "on_time":
      return "On Time";
    case "behind":
      return "Behind";
    case "at_risk":
      return "At Risk";
    case "late_departing":
      return "Late Departing";
    case "not_found":
      return "Not Found";
    default:
      return progressStatus;
  }
};

export const FLEET_STATUS_TYPE = {
  INACTIVE:'INACTIVE',
  OPERATIONAL:'OPERATIONAL',
  ACTIVE:'ACTIVE',
  GROUNDED:'GROUNDED'
}

export const PAYROLL_PROVIDER = {
  PAYCOM: "Paycom",
  ADP: "ADP",
};

export const ORGANIZATION_PAYROLL_PROVIDERS = {
  PAYCOM: "Paycom",
  ADP: "ADP",
  OTHER: "Other"
};

export const PAYROLL_PAYCODE = {
  PTO: "PTO",
  TRN: "TRN",
  TRAINING: "TRAINING"
};

export const PAYROLL_ID_STATUS_TYPE = {
  Active: 'ACTIVE',
  Inactive: 'INACTIVE',
  Offboarded: 'OFFBOARDED',
}

export const CONTACT_SALES_CALENDLY_URL = "https://calendly.com/neelshah-connect/linqops-demo";

// Subscription Constants
export const SUBSCRIPTION = {
  FREE_TRIAL: {
    PLAN_TITLE: "Business Pro",
    DURATION_DAYS: 31, // Actual trial duration (31 days to ensure Stripe shows at least 30 days)
    DISPLAY_DURATION_DAYS: 30, // Marketing duration shown to users
    COUPON_NAME: "Free Trial",
    COUPON_DESCRIPTION: "100% off for 30-day free trial"
  }
}

export const DRUG_TEST_RESULT_OPTIONS = [
  "Ordered",
  "At Lab",
  "Negative",
  "Positive",
  "Repeat",
]

export const INTEGRATIONS_TYPE = {
  PAYCOM: "paycom",
  ADP: "adp",
  ZOHO_MAIL: "zoho_mail",
}

// Integration credential field requirements
export const INTEGRATION_CREDENTIAL_FIELDS = {
  PAYCOM: {
    CREDENTIALS: ['sid', 'token'],
  },
  ADP: {
    CREDENTIALS: ['client_id', 'client_secret'],
    CERTIFICATES: ['certificate_pem', 'private_key'],
  },
}
// Notification throttling time in minutes
export const NOTIFICATION_THROTTLE_TIME_MINUTES = 15;

export const ORGANIZATION_TYPE = {
  PROVIDER: "provider",
}



// Email domain restriction for LINQOPS_ADMIN role
export const LINQOPS_ADMIN_EMAIL_DOMAIN = "@linqops.com";

// SMS-Related Keywords
// 10DLC Keywords
export const SMS_KEYWORDS = {
  OPT_IN: ['START','SUBSCRIBE'],
  OPT_OUT: ['STOP', 'UNSUBSCRIBE'],
  HELP: ['HELP'],
};

// Tollfree Keywords
export const TOLLFREE_SMS_KEYWORDS = {
  OPT_IN: ['START','UNSTOP'],
  OPT_OUT: ['STOP'],
  HELP: ['HELP'],
};

// Tenure Filter Ranges (in months)
export const TENURE_FILTERS = {
  NEW_HIRE: {
    MIN: 0,
    MAX: 2,
    LABEL: '0-2 months'
  },
  EARLY_TENURE: {
    MIN: 2,
    MAX: 4,
    LABEL: '2-4 months'
  },
  MID_TENURE: {
    MIN: 4,
    MAX: 6,
    LABEL: '4-6 months'
  },
  EXPERIENCED: {
    MIN: 6,
    MAX: null, // No upper limit
    LABEL: '6+ months'
  },
  OTHER: {
    LABEL: 'Others' // For cases where hire date is missing or invalid
  }
};

export const EMPLOYEE_VAN_PREFERENCE = {
  SELECT: "Select",
  AMAZON_BRANDED: "Amazon Branded",
  RENTAL: "Rental",
}

// Payroll Sync Status Constants
export const PAYROLL_SYNC_TYPE = {
  ADP: "adp",
  PAYCOM: "paycom",
};

// Payroll Sync Schedule Hours (UTC times for cron jobs)
// Paycom runs at 13:00 and 00:00 UTC
// ADP runs at 14:00 and 01:00 UTC
export const PAYROLL_SYNC_SCHEDULE_HOURS = {
  PAYCOM: [13, 0], // 13:00 and 00:00 UTC
  ADP: [14, 1], // 14:00 and 01:00 UTC
} as const;

export const PAYROLL_SYNC_STATUS = {
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed",
};

export const FIREBASE_APP_INSTANCE_NAMES = {
  DEFAULT: "[DEFAULT]",
  ADMIN_SUPPORT: "adminSupport",
}